import React, { useState } from 'react';
import { firestore } from '../../firebase/config';
import { collection, addDoc, doc, updateDoc, Timestamp } from 'firebase/firestore';
import { useTodasLocalidades } from '../../hooks/useLocalidades';
import '../../pages/Dashboard.css';

const ZONA_MAP = { '1': 'Urbana', '2': 'Rural' };

const estadoInicialForm = { nome: '', categoria: '', zona: '1' };

function GerenciarLocalidades() {
  const { localidades, loading, error, refetch } = useTodasLocalidades();

  const [showModal, setShowModal] = useState(false);
  const [editando, setEditando] = useState(null); // null = novo, objeto = editar
  const [form, setForm] = useState(estadoInicialForm);
  const [salvando, setSalvando] = useState(false);
  const [filtro, setFiltro] = useState('ativas'); // 'ativas' | 'inativas' | 'todas'

  // --- Handlers do formulário ---
  const abrirCriar = () => {
    setEditando(null);
    setForm(estadoInicialForm);
    setShowModal(true);
  };

  const abrirEditar = (loc) => {
    setEditando(loc);
    setForm({ nome: loc.nome, categoria: loc.categoria, zona: loc.zona });
    setShowModal(true);
  };

  const fecharModal = () => {
    setShowModal(false);
    setEditando(null);
    setForm(estadoInicialForm);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    const nome = form.nome.trim().toUpperCase();
    if (!nome || !form.categoria.trim() || !form.zona) {
      alert('Preencha todos os campos obrigatorios.');
      return;
    }

    setSalvando(true);
    try {
      if (editando) {
        await updateDoc(doc(firestore, 'localidades', editando.id), {
          nome,
          categoria: form.categoria.trim(),
          zona: form.zona,
          atualizadoEm: Timestamp.now(),
        });
      } else {
        await addDoc(collection(firestore, 'localidades'), {
          nome,
          categoria: form.categoria.trim(),
          zona: form.zona,
          ativo: true,
          criadoEm: Timestamp.now(),
          atualizadoEm: Timestamp.now(),
        });
      }
      fecharModal();
      refetch();
    } catch (err) {
      console.error('Erro ao salvar localidade:', err);
      alert('Erro ao salvar localidade. Tente novamente.');
    } finally {
      setSalvando(false);
    }
  };

  const handleToggleAtivo = async (loc) => {
    const acao = loc.ativo ? 'desativar' : 'reativar';
    const msg = loc.ativo
      ? `Desativar "${loc.nome}"? Ela nao aparecera mais no formulario do agente, mas os dados historicos serao preservados.`
      : `Reativar "${loc.nome}"? Ela voltara a aparecer no formulario do agente.`;

    if (!window.confirm(msg)) return;

    try {
      await updateDoc(doc(firestore, 'localidades', loc.id), {
        ativo: !loc.ativo,
        atualizadoEm: Timestamp.now(),
      });
      refetch();
    } catch (err) {
      console.error(`Erro ao ${acao} localidade:`, err);
      alert(`Erro ao ${acao} localidade.`);
    }
  };

  // --- Filtragem ---
  const localidadesFiltradas = localidades.filter(loc => {
    if (filtro === 'ativas') return loc.ativo;
    if (filtro === 'inativas') return !loc.ativo;
    return true;
  });

  const contAtivas = localidades.filter(l => l.ativo).length;
  const contInativas = localidades.filter(l => !l.ativo).length;

  return (
    <div style={{ padding: '0 0 40px 0' }}>
      {/* Cabecalho */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ margin: 0, color: '#1a1a2e', fontSize: '1.4rem' }}>Gerenciar Localidades (Bairros)</h2>
          <p style={{ margin: '6px 0 0', color: '#666', fontSize: '0.9rem' }}>
            Cadastre, edite ou desative localidades. Localidades inativas nao aparecem no formulario do agente, mas os dados historicos sao preservados.
          </p>
        </div>
        <button
          onClick={abrirCriar}
          style={{
            background: 'linear-gradient(135deg, #2196f3, #1565c0)',
            color: '#fff', border: 'none', borderRadius: '8px',
            padding: '10px 20px', fontSize: '0.95rem', cursor: 'pointer',
            fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px',
            boxShadow: '0 2px 8px rgba(33,150,243,0.4)',
            transition: 'transform 0.15s, box-shadow 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(33,150,243,0.5)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(33,150,243,0.4)'; }}
        >
          <span style={{ fontSize: '1.1rem' }}>+</span> Nova Localidade
        </button>
      </div>

      {/* Cards de resumo */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        {[
          { label: 'Total', valor: localidades.length, cor: '#1565c0', bg: '#e3f2fd' },
          { label: 'Ativas', valor: contAtivas, cor: '#2e7d32', bg: '#e8f5e9' },
          { label: 'Inativas', valor: contInativas, cor: '#c62828', bg: '#ffebee' },
        ].map(card => (
          <div key={card.label} style={{
            background: card.bg, border: `1px solid ${card.cor}33`,
            borderRadius: '10px', padding: '14px 24px', minWidth: '100px', textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.6rem', fontWeight: '700', color: card.cor }}>{card.valor}</div>
            <div style={{ fontSize: '0.8rem', color: '#555', fontWeight: '500' }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {[
          { valor: 'ativas', label: 'Ativas' },
          { valor: 'inativas', label: 'Inativas' },
          { valor: 'todas', label: 'Todas' },
        ].map(f => (
          <button
            key={f.valor}
            onClick={() => setFiltro(f.valor)}
            style={{
              padding: '6px 16px', borderRadius: '20px', border: '1px solid #ccc',
              fontSize: '0.85rem', cursor: 'pointer', fontWeight: filtro === f.valor ? '600' : '400',
              background: filtro === f.valor ? '#1565c0' : '#fff',
              color: filtro === f.valor ? '#fff' : '#555',
              transition: 'all 0.2s',
            }}
          >{f.label}</button>
        ))}
      </div>

      {/* Estado de carregamento / erro */}
      {loading && <p style={{ textAlign: 'center', color: '#999', padding: '40px' }}>Carregando localidades...</p>}
      {error && <p style={{ textAlign: 'center', color: '#c62828', padding: '20px' }}>{error}</p>}

      {/* Tabela */}
      {!loading && !error && (
        <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #e0e0e0', boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ background: 'linear-gradient(135deg, #1a237e, #283593)', color: '#fff' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Nome da Localidade</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600' }}>Categoria</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600' }}>Zona</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: '600' }}>Acoes</th>
              </tr>
            </thead>
            <tbody>
              {localidadesFiltradas.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    Nenhuma localidade encontrada.
                  </td>
                </tr>
              )}
              {localidadesFiltradas.map((loc, idx) => (
                <tr
                  key={loc.id}
                  style={{
                    background: idx % 2 === 0 ? '#fff' : '#f8f9fa',
                    opacity: loc.ativo ? 1 : 0.6,
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = '#e8f0fe'}
                  onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#f8f9fa'}
                >
                  <td style={{ padding: '11px 16px', fontWeight: '500', color: loc.ativo ? '#1a1a2e' : '#999' }}>
                    {loc.nome}
                  </td>
                  <td style={{ padding: '11px 16px', textAlign: 'center', color: '#555' }}>
                    {loc.categoria}
                  </td>
                  <td style={{ padding: '11px 16px', textAlign: 'center', color: '#555' }}>
                    <span style={{
                      background: loc.zona === '1' ? '#e3f2fd' : '#fff8e1',
                      color: loc.zona === '1' ? '#1565c0' : '#e65100',
                      padding: '2px 10px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '600'
                    }}>
                      {ZONA_MAP[loc.zona] || loc.zona}
                    </span>
                  </td>
                  <td style={{ padding: '11px 16px', textAlign: 'center' }}>
                    <span style={{
                      background: loc.ativo ? '#e8f5e9' : '#ffebee',
                      color: loc.ativo ? '#2e7d32' : '#c62828',
                      padding: '2px 12px', borderRadius: '12px', fontSize: '0.8rem', fontWeight: '600'
                    }}>
                      {loc.ativo ? 'Ativa' : 'Inativa'}
                    </span>
                  </td>
                  <td style={{ padding: '11px 16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={() => abrirEditar(loc)}
                        style={{
                          background: '#1565c0', color: '#fff', border: 'none',
                          borderRadius: '6px', padding: '5px 12px', fontSize: '0.8rem',
                          cursor: 'pointer', fontWeight: '500',
                        }}
                      >Editar</button>
                      <button
                        onClick={() => handleToggleAtivo(loc)}
                        style={{
                          background: loc.ativo ? '#c62828' : '#2e7d32',
                          color: '#fff', border: 'none', borderRadius: '6px',
                          padding: '5px 12px', fontSize: '0.8rem', cursor: 'pointer', fontWeight: '500',
                        }}
                      >{loc.ativo ? 'Desativar' : 'Reativar'}</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de criacao / edicao */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 9999, padding: '16px',
        }}>
          <div style={{
            background: '#fff', borderRadius: '14px', padding: '32px',
            width: '100%', maxWidth: '480px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            animation: 'fadeInUp 0.2s ease',
          }}>
            <h3 style={{ margin: '0 0 20px', color: '#1a237e', fontSize: '1.2rem' }}>
              {editando ? 'Editar Localidade' : 'Nova Localidade'}
            </h3>

            <form onSubmit={handleSalvar}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#333', fontSize: '0.9rem' }}>
                  Nome completo * <span style={{ fontWeight: '400', color: '#888' }}>(ex: 58 - NOVO BAIRRO)</span>
                </label>
                <input
                  name="nome"
                  value={form.nome}
                  onChange={handleChange}
                  required
                  placeholder="N - NOME DO BAIRRO"
                  style={{
                    width: '100%', padding: '10px 12px', border: '1px solid #ccc',
                    borderRadius: '8px', fontSize: '0.95rem', boxSizing: 'border-box',
                    outline: 'none', textTransform: 'uppercase',
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#333', fontSize: '0.9rem' }}>
                    Codigo Categoria *
                  </label>
                  <input
                    name="categoria"
                    value={form.categoria}
                    onChange={handleChange}
                    required
                    placeholder="ex: 228"
                    style={{
                      width: '100%', padding: '10px 12px', border: '1px solid #ccc',
                      borderRadius: '8px', fontSize: '0.95rem', boxSizing: 'border-box',
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', fontWeight: '600', marginBottom: '6px', color: '#333', fontSize: '0.9rem' }}>
                    Zona *
                  </label>
                  <select
                    name="zona"
                    value={form.zona}
                    onChange={handleChange}
                    required
                    style={{
                      width: '100%', padding: '10px 12px', border: '1px solid #ccc',
                      borderRadius: '8px', fontSize: '0.95rem', boxSizing: 'border-box',
                    }}
                  >
                    <option value="1">1 - Urbana</option>
                    <option value="2">2 - Rural</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button
                  type="button"
                  onClick={fecharModal}
                  disabled={salvando}
                  style={{
                    padding: '10px 20px', borderRadius: '8px', border: '1px solid #ccc',
                    background: '#fff', color: '#555', cursor: 'pointer', fontSize: '0.95rem',
                  }}
                >Cancelar</button>
                <button
                  type="submit"
                  disabled={salvando}
                  style={{
                    padding: '10px 24px', borderRadius: '8px', border: 'none',
                    background: 'linear-gradient(135deg, #2196f3, #1565c0)',
                    color: '#fff', cursor: 'pointer', fontSize: '0.95rem', fontWeight: '600',
                    opacity: salvando ? 0.7 : 1,
                  }}
                >{salvando ? 'Salvando...' : (editando ? 'Salvar Alteracoes' : 'Criar Localidade')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default GerenciarLocalidades;
